// Panel veri tipleri — api.ts'in döndürdüğü şekiller.
// Bu dosya SPA ile backend arasındaki sözleşmedir: Phase 3'te api.ts'in gövdeleri
// gerçek REST çağrılarıyla değişecek, bu tipler aynı kalacak (PLAN.md → "Frontend wiring").

// primevip = the Prime VIP line (2026-10-14): its own proxy + workbook, a brand of its own here.
export type BrandId = 'vegas' | 'prime' | 'primevip' | 'betbooker' | 'hera';
export type Lang = 'tr' | 'en';
export type Theme = 'dark' | 'light';
export type Role = 'admin' | 'supervisor';

export interface Brand {
  id: BrandId;
  name: string;
}

/** Durum anahtarları iki dilin birleşimi: TR markalar + BetBooker (EN). */
export type StatusKey =
  // current TR set (2026-09-09)
  | 'positive'
  | 'not_interested'
  | 'hung_up_brand'
  | 'call_back'
  | 'no_answer'
  | 'line_busy'
  | 'wrong_number'
  | 'do_not_call'
  | 'no_call'
  // TR, added 2026-09-14 (grid positions 10-11)
  | 'answered_by_other'
  | 'hung_up_mid_call'
  // EN-only
  | 'na'
  // legacy TR canons (historical reports)
  | 'answered'
  | 'unreachable'
  | 'busy'
  | 'dissatisfied'
  // catch-all for a proxy-added result the server has not learned yet (engine UNKNOWN_CANON)
  | 'other';

export interface StatusDef {
  key: StatusKey;
  label: string;
  color: string;
}

export interface StatusSystems {
  tr: StatusDef[];
  en: StatusDef[];
  palette: Record<string, string>;
}

/** Oturum açmış panel kullanıcısı (şifre asla taşınmaz). */
export interface PanelUser {
  username: string;
  displayName: string;
  role: Role;
  roleLabel: string;
  brands: BrandId[];
  active: boolean;
  /** Kişi bazlı sayfa bayrakları (sunucu src/http/session.js featuresOf). Eski bir localStorage
   *  oturumunda eksik olabilir → App yalnız `features?.winback === true` ile gösterir; /me
   *  tazelemesi ilk boyamada alanı getirir. Sunucu kapısı rotanın kendisidir (403). */
  features?: { winback?: boolean };
}

// ── Canlı Durum ──────────────────────────────────────────────────────────────

export type AgentState = 'working' | 'break' | 'offline';

// ── Vardiya planı (shift roster) ─────────────────────────────────────────────
// 'off' = izinli ('-'), 'tl' = takım lideri görevi — ikisi de o gün ajan çağrısı
// beklenmediği anlamına gelir ve uyarıları susturur.
// 'fixed' = markanın vardiya planı YOK ama sabit bir çalışma penceresi var (Vegas/Prime
// 10:30–18:30 İstanbul). Sunucuda okuma anında üretilir, ASLA saklanmaz → uyarı üretmez
// ve uyarı susturmaz; yalnızca gösterim içindir. DB enum'unda karşılığı yoktur.
export type ShiftKind = 'morning' | 'evening' | 'off' | 'tl' | 'fixed';

/** Plandaki GELEN ÇAĞRI görevi (migration 015). '' = yok — sentinel boş dizge (§6).
 *  'morning_inbound' = tüm gün santralden gelen çağrı (o gün HİÇ uyarı üretilmez);
 *  'evening_inbound' = 14:00–18:00 arama, 18:00'den sonra gelen çağrı (o saatten
 *  sonrası için uyarı üretilmez). Vardiyadan BAĞIMSIZ: `shift` ne zaman işte
 *  olduğunu, bu alan o gün ne yaptığını söyler. */
export type InboundDuty = '' | 'morning_inbound' | 'evening_inbound';

export interface AgentSchedule {
  shift: ShiftKind;
  label: string;
  /** 'HH:MM' — off/tl için null. */
  startAt: string | null;
  endAt: string | null;
  /** Plandaki görev metni, ör. 'EVENING INBOUND - REG'. */
  task: string;
  /** 'CALL AGENT' / 'CALL TEAM LEAD'. */
  role: string;
  inboundDuty: InboundDuty;
  /** 'Sabah gelen çağrı' / 'Akşam gelen çağrı', görev yoksa ''. */
  inboundLabel: string;
  /** ŞU AN gelen çağrı penceresinde mi (yalnız Canlı Durum doldurur; geçmiş
   *  günlerde her zaman false — rozet görevi yine de adlandırır). */
  inboundActive: boolean;
}

/** shift 'off'|'tl' → o gün çalışmıyor (uyarı üretilmez). */
export function isOffDuty(s: AgentSchedule | null | undefined): boolean {
  return s?.shift === 'off' || s?.shift === 'tl';
}

export interface LiveAgent {
  id: string;
  name: string;
  department: string;
  brands: BrandId[];
  state: AgentState;
  todayCalls: number;
  firstCallAt: string;
  lastCallAt: string;
  terminalOpenAt: string;
  breakRemainingMin: number;
  breakStartedAt?: string;
  lastSeenAt: string | null;
  /** En son heartbeat'in ham sunucu saati (yoksa null). `lastSeenAt`ten farklı: o, çağrı olayları
   *  ve mola başlangıcını da katar; bu yalnız canlı heartbeat — yani komutu alabilecek tek sinyal. */
  lastBeatAt: string | null;
  /** Panel "karanlık" mı: temsilci mevcut görünüyor (çalışıyor/molada) ama heartbeat susmuş, yani
   *  şu an gönderilecek bir uzaktan-destek komutu ULAŞMAZ. Süpervizöre "komut yerine telefon aç"
   *  der. 'offline' zaten gitmiş okunur, o yüzden ayrıca karanlık işaretlenmez. */
  panelDark: boolean;
  /** null = markanın vardiya planı kaynağı yok (Vegas/Prime). */
  schedule: AgentSchedule | null;
  /**
   * Müşteri işlemesinin başladığı an (ISO), yoksa null. Eklentinin `AwaitingOutcome`
   * FSM durumundan gelir: müşteri açık ve sonuç henüz girilmemiş — yani görüşme VE
   * sonrasındaki not yazımı. Bayat heartbeat'te null'a döner (bkz. engine/presence.js),
   * o yüzden kapanan bir tarayıcı kartı sonsuza dek "işlemde" bırakamaz.
   * `state` alanına DÖRDÜNCÜ bir değer eklemek yerine ayrı alan: state hem sıralama
   * hem özet sayaçlarını besliyor, yeni bir üye hepsini sessizce bozardı.
   */
  handlingSince: string | null;
  /** Eklentinin kendi sağlık sayaçları (bugün). Satır yoksa hepsi 0. */
  telemetry: AgentTelemetry;
  /** En son görülen eklenti sürümü (`agent_build_seen`, migration 029); hiç görülmediyse ''.
   *  Eski sunucu alanı göndermez → undefined = ''. */
  extVersion?: string;
  /** O sürümün son görüldüğü sunucu anı (5 dk kısıtlı); yoksa null. */
  extVersionSeenAt?: string | null;
  /** Eklentinin son 1 saatte gönderdiği HATA satırı (2.3.0+, migration 030; eski sürüm/sunucu = 0 ya da yok). */
  extErrors1h?: number;
  /**
   * ÖLÇÜLEN çağrı (softphone call_sessions, migration 029): temsilci şu an telefonda mı.
   * `state`'e 4. değer DEĞİL, `handlingSince` gibi ayrı alan (§7). Kart sırası: mola >
   * onCall > müşteri işlemde. Eski sunucu alanı hiç göndermez → undefined = null.
   */
  onCall?: LiveOnCall | null;
  /** Bugünkü softphone sayaçları — YALNIZ temsilcinin bugün ≥1 oturumu varsa gelir
   *  (eski eklenti sürümünde anahtar YOK: ölçülmedi ≠ sıfır). */
  dialsToday?: number;
  connectedToday?: number;
  talkSecToday?: number;
}

/** Canlı kartın çağrı bilgisi (sözleşme C9). `since` sunucu saatine çevrilmiş faz başlangıcı
 *  (talking = cevaplandığı an, ringing = arandığı an); gelecekteyse kart `receivedAt`'ten sayar. */
export interface LiveOnCall {
  since: string;
  receivedAt: string;
  phase: 'ringing' | 'talking';
  callId: string;
  customerUserId: string;
}

/**
 * Eklentinin kendi bildirdiği sağlık göstergeleri (migration 016/019). Hepsi GÜN başına
 * kümülatif; satır yoksa 0 döner ("veri yok" ile "sorun yok" okuyucu için aynı şey).
 * `deadLetters > 0` = o gün panele HİÇ ulaşmamış çağrı sonucu var, yani temsilcinin
 * toplamları o kadar EKSİK; `panelSleepMaxMs` = sekmenin en uzun donma süresi.
 */
export interface AgentTelemetry {
  deadLetters: number;
  writeErrors: number;
  walReplays: number;
  panelSleepMaxMs: number;
  /** 'tab_frozen' | 'device_suspended' | 'unknown' — bilinmiyorsa boş dizge. */
  panelSleepCause: string;
  /** Migration 029 göstergeleri (2.1.0+; eski sürüm/sunucu = 0 ya da yok). */
  callEventDrops?: number;
  authLandings?: number;
}

export interface ScheduleRow extends AgentSchedule {
  brand: BrandId;
  agentId: string | null;
  name: string;
  /** false = plandaki isim hiçbir temsilciyle eşleşmedi. */
  matched: boolean;
  syncedAt: string | null;
}

export interface ScheduleDay {
  date: string;
  brands: BrandId[];
  rows: ScheduleRow[];
}

export interface ScheduleBrandState {
  lastRunDay?: string;
  lastOkAt?: string;
  weekStart?: string;
  days?: string[];
  rows?: number;
  agents?: number;
  /** Boş bırakılmış hücre sayısı — satır üretmez (boş = bilgi yok, izinli DEĞİL). */
  blankCells?: number;
  unmatched?: string[];
  staleWeek?: boolean;
  warnings?: string[];
}

export interface ScheduleState {
  enabled: boolean;
  brands: BrandId[];
  tab: string;
  /** Günlük okuma saati (Europe/Istanbul). */
  syncAt: string;
  state: Partial<Record<BrandId, ScheduleBrandState>>;
}

// ── Raporlar ─────────────────────────────────────────────────────────────────

export type Granularity = 'daily' | 'weekly' | 'monthly';
export type ReportPreset = 'today' | 'yesterday' | 'this_week' | 'last_week' | 'this_month' | 'custom';

export interface DateRange {
  start: string;
  end: string;
}

export interface ReportList {
  key: string;
  list: string;
  manual: boolean;
  total: number;
  results: Partial<Record<StatusKey, number>>;
}

export interface WaitStat {
  avgSec: number;
  medianSec: number;
}

export interface ReportWait {
  lazinessAvgSec: number;
  lazinessThresholdSec: number;
  longGaps: number;
  afterResult: Partial<Record<StatusKey, WaitStat>>;
}

export interface ReportBucket {
  key: string;
  label: string;
  calls: number;
}

export interface AgentReport {
  agentId: string;
  name: string;
  department: string;
  brands: BrandId[];
  lang: Lang;
  totalCalls: number;
  answered: number;
  answerRate: number;
  /** Softphone ÖLÇÜMÜ (agent_day_summary, migration 029) — dönemde hiç oturumu olmayan temsilcide
   *  hepsi null (boş basılır, 0 DEĞİL — §6). Oranlar paydasızken null. */
  dials: number | null;
  connected: number | null;
  connectRate: number | null;
  avgTalkSec: number | null;
  talkTotalSec: number | null;
  lists: ReportList[];
  wait: ReportWait;
  buckets: ReportBucket[];
}

/** p50/p95 (ms) — nearest-rank; n = örnek sayısı. */
export interface CyclePercentiles {
  p50Ms: number;
  p95Ms: number;
  n: number;
}

/** GET /api/report/team — temsilci satırlarından HAVUZLANAMAYAN ekip rakamları. */
export interface TeamReport {
  /** Müşteri gösterildi → sonuç kaydedildi (eklenti ölçümü); örnek yoksa null. */
  cycle: CyclePercentiles | null;
  dials: number | null;
  connected: number | null;
  connectRate: number | null;
  avgTalkSec: number | null;
  talkTotalSec: number | null;
}

// ── Temsilci Detayı ──────────────────────────────────────────────────────────────

export interface RosterAgent {
  id: string;
  name: string;
  department: string;
  brands: BrandId[];
  darkCode: string;
}

/** Başka bir sayfadan Temsilci Detayı'na atlama isteği. `n` nonce'tur: aynı
 *  temsilci ikinci kez seçilse bile prop değişsin (effect tetiklensin) diye. */
export interface AgentFocus {
  id: string;
  n: number;
}

export type CallSource = 'tool' | 'manual';

export interface CallLogEntry {
  id: string;
  min: number;
  at: string;
  listKey: string;
  list: string;
  customerId: string;
  result: StatusKey;
  comment: string;
  source: CallSource;
  /** Bu sonucun arkasındaki ÖLÇÜLEN çağrı (call_sessions). ISO anlar sunucu saatinde; oturum
   *  yoksa '' / null (eski sürüm, manuel sekme, Arama Yapılmadı) — boş basılır. */
  dialAt?: string;
  answeredAt?: string;
  endedAt?: string;
  ringSec?: number | null;
  talkSec?: number | null;
  cause?: string;
  hangupBy?: string;
}

export interface TimelineBreak {
  startMin: number;
  endMin: number;
  startAt: string;
  endAt: string;
  durationMin: number;
}

export interface TimelineGap {
  startMin: number;
  endMin: number;
  startAt: string;
  endAt: string;
  durationMin: number;
}

/** Zaman çizelgesi oturum çubuğu (API döner; SPA zaman çizelgesi 2026-09-13'ten beri çizilmiyor). */
export interface TimelineSession {
  startMin: number;
  endMin: number;
  startAt: string;
  endAt: string;
  answered: boolean;
  open: boolean;
  talkSec: number | null;
}

export interface AgentDayTimeline {
  startMin: number;
  endMin: number;
  terminalOpenMin: number;
  calls: CallLogEntry[];
  breaks: TimelineBreak[];
  gaps: TimelineGap[];
  sessions?: TimelineSession[];
}

/** Döngü kartı — her metrik o gün hiçbir sonuç taşımadıysa null. */
export interface AgentDayCycle {
  shownToSave: CyclePercentiles | null;
  fetchUser: CyclePercentiles | null;
  reload: CyclePercentiles | null;
}

export interface ReconSessionItem {
  callId: string;
  at: string;
  customerId: string;
  answered: boolean;
  talkSec: number | null;
  open: boolean;
}

export interface ReconOutcomeItem {
  id: string;
  at: string;
  customerId: string;
  result: StatusKey;
}

/** Oturum ↔ sonuç mutabakatı; o gün hiç oturum yoksa null (eski sürüm — ölçülmedi). */
export interface AgentDayReconciliation {
  sessions: number;
  outcomes: number;
  sessionsWithoutOutcomeCount: number;
  outcomesWithoutSessionCount: number;
  sessionsWithoutOutcome: ReconSessionItem[];
  outcomesWithoutSession: ReconOutcomeItem[];
}

export interface AgentDaySummary {
  totalCalls: number;
  firstCallAt: string;
  lastCallAt: string;
  terminalOpenAt: string;
  breakUsedMin: number;
  breakCount: number;
}

export interface AgentDayWaitStat {
  result: StatusKey;
  label: string;
  color: string;
  count: number;
  avgSec: number;
  medianSec: number;
}

export interface AgentDayDark {
  terminalTotal: number;
  darkTotal: number;
  diff: number;
  /** DARK 'Toplam Arama Süresi' — çalma DAHİL başlangıç→bitiş (konuşma süresi değil). */
  darkTalkSec: number;
  /** Softphone çalma + konuşma toplamı (sn); o gün biten oturum yoksa null. */
  terminalTalkRingSec?: number | null;
  terminalFirst: string;
  terminalLast: string;
  darkFirst: string;
  darkLast: string;
}

/** Temsilci Detayı haftalık kart — bir günün SHEET dilimi (haftalık rapor taraması, §19).
 *  `null` = rapor o günü hiç görmedi (satır yok ≠ sıfır). Para HAM (dedup'suz). */
export interface AgentWeekSheetDay {
  conversations: number;
  /** BAŞARI paydası = görüşme − DAVET (görev #297). Alan yoksa eski sunucu → `conversations`. */
  successBase?: number;
  deposited12h: number;
  deposited36h: number;
  deposit12h: number;
  deposit36h: number;
  pending: number;
}

export type AgentWeekSheetStatus = 'ok' | 'not_in_report' | 'excluded' | 'unsupported' | 'busy' | 'error';

export interface AgentWeek {
  agentId: string;
  weekStart: string;
  weekEnd: string;
  days: {
    day: string;
    /** Bu app'in Postgres'i (agent_day_summary) — üst karttaki TOPLAM ÇAĞRI ile aynı kaynak. */
    calls: number;
    answered: number;
    /** Softphone ölçümü; o gün oturum yoksa null (boş — 0 DEĞİL). */
    dials?: number | null;
    connected?: number | null;
    talkSec?: number | null;
    sheet: AgentWeekSheetDay | null;
  }[];
  totals: {
    calls: number;
    answered: number;
    dials?: number | null;
    connected?: number | null;
    talkSec?: number | null;
    sheet: AgentWeekSheetDay | null;
  };
  sheet: {
    status: AgentWeekSheetStatus;
    brands: BrandId[];
    settled: boolean;
    error?: { kind: string; message: string } | null;
    partialError?: { kind: string; message: string } | null;
  };
}

export interface AgentDayDetail {
  agent: {
    id: string;
    name: string;
    department: string;
    brands: BrandId[];
    darkCode: string;
    lang: Lang;
  };
  date: string;
  /** O GÜNÜN vardiya planı; null = markanın planı yok ya da o gün anlık görüntüde değil. */
  schedule: AgentSchedule | null;
  summary: AgentDaySummary;
  /** Eklenti sağlık sayaçları — `summary` rakamlarının neden eksik olabileceğini açıklar. */
  telemetry: AgentTelemetry;
  timeline: AgentDayTimeline;
  log: CallLogEntry[];
  waitStats: AgentDayWaitStat[];
  /** O gün bu temsilcinin gönderdiği SMS'ler (yeni→eski); boş = o gün SMS yok / marka relay etmedi. */
  sms: AgentSmsEntry[];
  /** Döngü kartı (eski sunucuda yok). */
  cycle?: AgentDayCycle;
  /** Oturum ↔ sonuç mutabakatı; null = o gün oturum yok. */
  reconciliation?: AgentDayReconciliation | null;
  dark: AgentDayDark;
}

// ── Molalar ──────────────────────────────────────────────────────────────────

export type BreakEndReason = 'Temsilci' | 'Zaman aşımı' | 'Admin' | 'Gece yarısı';

export interface BreakSession {
  startMin: number;
  endMin: number;
  startAt: string;
  endAt: string;
  durationMin: number;
  endReason: BreakEndReason;
  idleBeforeMin: number;
  idleFlag: boolean;
}

export interface BreakDayRow {
  agentId: string;
  name: string;
  department: string;
  brands: BrandId[];
  allowanceMin: number;
  grantedExtra: number;
  usedMin: number;
  remainingMin: number;
  overageMin: number;
  sessionCount: number;
  sessions: BreakSession[];
}

export interface OnBreakNow {
  id: string;
  name: string;
  department: string;
  startAt: string;
  startMs: number;
  allowanceMin: number;
  /** Break minutes this agent already used TODAY on this session's brand (closed sessions only).
   *  The card adds the running session live from startMs; without this it measured one session
   *  against the whole daily allowance and showed green while the agent was already over. */
  usedBeforeMin?: number;
}

export interface BreaksData {
  date: string;
  onBreakNow: OnBreakNow[];
  rows: BreakDayRow[];
}

export interface GrantResult {
  agentId: string;
  grantedExtra: number;
  added: number;
  reason: string;
}

// ── Uyarılar ─────────────────────────────────────────────────────────────────

export type AlertRuleType =
  | 'mola_oncesi_bosta'
  | 'mola_asimi'
  | 'dark_uyusmazligi'
  | 'dark_eksik'
  | 'uzun_bosluk'
  | 'bilinmeyen_ajan'
  | 'sync_donduruldu'
  | 'mola_otomatik_kapandi'
  | 'bilinmeyen_durum'
  | 'plan_gelmedi'
  | 'plan_disi_calisma'
  | 'plan_eslesmeyen_ajan'
  | 'plan_guncel_degil'
  | 'saat_sapmasi'
  | 'sonuc_kaybi'
  | 'dahili_uyusmazligi'
  | 'cagri_hedefi_alti'
  | 'dusuk_yanit_orani';

export type Severity = 'info' | 'warning' | 'critical';
export type AlertStatus = 'new' | 'ack';

/** payload: [etiket, değer] çiftleri — genişletilmiş satırda kart olarak gösterilir. */
export type AlertPayload = [string, string][];

/** Kural tipi → CANLI eşiklerle üretilmiş başlık (GET /api/alert-rules).
 *  Eşik alıntılayan başlıklar (`uzun_bosluk` → "Uzun boşluk (>15 dk)") sunucuda
 *  `config_kv.thresholds`'ten üretilir; constants.ts RULE_TITLES yalnızca
 *  varsayılan-eşik yedeğidir. Sunucu eşi: src/engine/status-map.js alertRuleTitles. */
export type AlertRuleTitles = Record<AlertRuleType, string>;

export interface Alert {
  id: string;
  ruleType: AlertRuleType;
  title: string;
  severity: Severity;
  icon: string;
  agentId: string | null;
  agentName: string;
  brand: BrandId;
  description: string;
  payload: AlertPayload;
  /** Uyarının AİT OLDUĞU gün ("YYYY-MM-DD", markanın kendi günü) — `createdAt` ise
   *  motorun onu FARK ETTİĞİ an. İkisi rutin olarak ayrışır: boşluk yargısı en yavaş
   *  ingest yolunu bekler, eşik düşünce eski günler yeniden hesaplanır. Gün boyutu
   *  olmayan kurallarda boştur (`bilinmeyen_ajan` vb.) — yer tutucu YOK. */
  dayIst: string;
  createdMs: number;
  createdAt: string;
  status: AlertStatus;
  ackAt: string | null;
  ackBy: string | null;
  /** Onaylayanın açıklaması (zorunlu, 2026-09-13). Onaylı satırda `''` = migration 026
   *  öncesi onay (açıklama henüz yoktu) — yer tutucu basılmaz (§6). */
  ackNote: string;
}

export interface AlertFilter {
  brands?: BrandId[];
  ruleType?: AlertRuleType | 'all';
  agentId?: string | 'all';
  brand?: BrandId | 'all';
  start?: string;
  end?: string;
  status?: AlertStatus | 'all';
}

// ── DARK ─────────────────────────────────────────────────────────────────────

export interface DarkUpload {
  id: string;
  fileName: string;
  date: string;
  rowCount: number;
  uploadedBy: string;
  uploadedAt: string;
}

export interface DarkRow {
  agentId: string;
  name: string;
  department: string;
  brands: BrandId[];
  darkCode: string;
  terminalTotal: number;
  darkTotal: number;
  diff: number;
  over: boolean;
  darkTalkSec: number;
  terminalFirst: string;
  terminalLast: string;
  darkFirst: string;
  darkLast: string;
  firstMismatch: boolean;
  lastMismatch: boolean;
}

export interface DarkUnmatched {
  darkCode: string;
  darkTotal: number;
  darkTalkSec: number;
}

export interface DarkCompare {
  date: string;
  rows: DarkRow[];
  unmatched: DarkUnmatched[];
  uploads: DarkUpload[];
}

// ── Ayarlar ──────────────────────────────────────────────────────────────────

export interface AgentRecord {
  id: string;
  name: string;
  darkCode: string;
  department: string;
  brands: BrandId[];
  /** marka → o markadaki terminal ad eşleşmeleri */
  aliases: Partial<Record<BrandId, string[]>>;
  active: boolean;
  temp: boolean;
  /** Takım lideri: bu temsilciden HİÇBİR uyarı üretilmez (migration 013). */
  teamLead: boolean;
  /** Ayarlar'dan kaldırıldı (migration 035): gizli + pasif, geçmişi duruyor; "Geri al" ile döner. */
  removed: boolean;
  /**
   * En son görülen eklenti sürümü + o günün tarihi (migration 019). Boş dizge = bu temsilciden
   * sürüm taşıyan bir terminal_open hiç gelmedi (eski sürümde DEĞİL — hiç açmamış demektir).
   * Chrome Web Store yayını elle yapıldığı için "güncelleme ajanlara ulaştı mı?" sorusunun
   * tek cevabı budur.
   */
  lastVersion: string;
  lastLang: string;
  lastVersionDay: string;
}

export interface Thresholds {
  idleBeforeBreakMin: number;
  darkDiff: number;
  longGapMin: number;
  dailyBreakMin: number;
  /** Mola hakkı aşıldığında uyarı üretmeden önce hoş görülen süre (dk). */
  breakGraceMin: number;
  /** Vardiya başlangıcından sonra "gelmedi" uyarısı için beklenen süre (dk). */
  noShowGraceMin: number;
  /** Günlük çağrı hedefi (sayılan çağrı); altında kalan çalışan temsilciye uyarı. 0 = kapalı. */
  dailyCallTarget: number;
  /** Günlük yanıtlanma oranı alt sınırı (%); altında kalana uyarı. 0 = kapalı. */
  minAnswerRatePct: number;
  /** Vardiya bitişinden sonra günün değerlendirilmesi için beklenen süre (dk). */
  dailyTargetGraceMin: number;
}

export interface LazinessEntry {
  key: StatusKey;
  included: boolean;
  label: string;
  color: string;
}

export interface LazinessConfig {
  tr: LazinessEntry[];
  en: LazinessEntry[];
}

export interface MutationResult<T = unknown> {
  ok: boolean;
  error?: string;
  user?: PanelUser;
  agent?: T;
  /** Marka değişti → panel girişi taşındı mı (src/api/panel-accounts.js). */
  panelSync?: PanelSyncResult;
  /** Geri al (İşten ayrıldı'nın tersi) → QMS / panel girişi adımları (src/api/offboard.js). */
  steps?: PanelSyncStep[];
}

/** İşten ayrıldı sebebi (src/api/offboard.js OFFBOARD_REASONS). */
export type OffboardReason = 'left' | 'fired';

/** POST /api/agents/:id/offboard yanıtı — adımlar sunucudan TR gelir. */
export interface OffboardResult {
  ok: boolean;
  error?: string;
  steps?: PanelSyncStep[];
}

/** Panel hesabı adımı — marka başına ne oldu (src/api/panel-accounts.js). Metin sunucudan TR gelir. */
export interface PanelSyncStep {
  brand: BrandId | '';
  status: 'done' | 'exists' | 'skipped' | 'warn' | 'error';
  text: string;
}

export interface PanelSyncResult {
  ok: boolean;
  steps: PanelSyncStep[];
}


// ── Uzaktan destek komutları (agent_commands, migration 014) ─────────────────
// Süpervizörün bir temsilcinin panelinde çalıştırabileceği SABİT komut listesi.
// Uzaktan kod çalıştırma DEĞİLDİR: her komut eklentinin kendi sürümünde derlenmiş
// bir 'case'tir; bilmediği bir komutu 'unsupported' olarak yanıtlar.
export type CommandTier = 1 | 2 | 3;

/** GET /api/agents/:id/ext-health — Eklenti Sağlığı çekmecesi (src/api/ext-health.js). */
export interface ExtHealthEvent {
  at: string;
  type: 'panel_open' | 'version' | 'alert' | 'report';
  source?: 'agent' | 'admin';
  note?: string;
  id?: string;
  version?: string;
  rule?: AlertRuleType;
  severity?: string;
  description?: string;
}

export interface ExtHealth {
  agentId: string;
  brandKey: string;
  diagnostics: { at: string | null; result: Record<string, unknown> } | null;
  logs: { at: string | null; lines: string[] } | null;
  commands: AgentCommand[];
  /** sunucu − temsilci saati (ms); 2.1.0 öncesi null. */
  clock: { offsetMs: number; at: string | null } | null;
  timeline: ExtHealthEvent[];
  /** Faz 2 (2.3.0+, migration 030). Eski sunucu bu alanları göndermez → yok. */
  errors?: ExtErrorGroup[];
  lines?: ExtLogLine[];
  reports?: ExtReportRow[];
  env?: ExtEnv | null;
}

export interface ExtEnv { installType: string; browser: string; os: string }

export interface ExtErrorGroup {
  fp: string;
  level: 'error' | 'warn' | 'info';
  cat: string;
  message: string;
  countToday: number;
  count7d: number;
  first: string | null;
  last: string | null;
  versions: string[];
  /** Yalnız filo görünümünde. */
  agents?: number;
  /** Yalnız filo: 7 İstanbul günü, eskiden bugüne. */
  daily?: number[];
}

export interface ExtLogLine {
  at: string;
  receivedAt: string;
  level: 'error' | 'warn' | 'info';
  cat: string;
  msg: string;
  fp: string;
  version: string;
}

export type ExtReportStatus = 'new' | 'looking' | 'fixed';

export interface ExtReportRow {
  id: string;
  agentId: string;
  agentName: string;
  brandKey: string;
  createdAt: string;
  source: 'agent' | 'admin';
  note: string;
  version: string;
  env: Partial<ExtEnv>;
  status: ExtReportStatus;
  handledBy: string;
  lineCount: number;
  bytes: number;
  /** 2.5.0 — yapıştırılan ekran görüntüsü sayısı (migration 036; 30 gün sonra 0). */
  imageCount?: number;
}

export interface ExtReport extends ExtReportRow {
  diagnostics: Record<string, unknown>;
  lines: string[];
  /** 2.5.0 — ekran görüntüleri (bayt GET /api/ext-reports/:id/images/:idx). */
  images?: { idx: number; size: number }[];
}

/** GET /api/ext-fleet — Eklenti sayfasının faz 2 kartları. */
export interface ExtFleet {
  errors: ExtErrorGroup[];
  reports: ExtReportRow[];
  /** agentId → ortam (2.3.0+). */
  env: Record<string, ExtEnv>;
}

export type CommandStatus =
  | 'pending' | 'delivered' | 'accepted' | 'done'
  | 'failed' | 'refused' | 'unsupported' | 'expired';

export interface AgentCommand {
  id: string;
  kind: string;
  args: Record<string, unknown>;
  status: CommandStatus;
  issued_by: string;
  issued_at: string;
  expires_at: string;
  delivered_at: string | null;
  accepted_at: string | null;
  done_at: string | null;
  result: Record<string, unknown> | null;
  /** "Bir sonraki açılışta uygula" (toolkit T2): TTL 24 sa. */
  deferred?: boolean;
}


// ── Haftalık Rapor (GET /api/weekly-report) ─────────────────────────────────
// Sahibin elle tuttuğu "VS" tablosunun panel karşılığı. Veri SHEET taramasından
// gelir, bu app'in Postgres'inden DEĞİL (sunucu eşi: src/engine/weekly-report.js) —
// böylece çağrı sayısı ile yatırım tutarı aynı taramadan çıkar ve birbirini tutar.
//
// OPSİYONEL/nullable alanlar KASITLI ve iki ayrı sebepten:
//   1. `messaging.byReason`, `depositTracked` — sunucu yarısı bu sayfayla EŞ ZAMANLI
//      yazılıyor; bir anahtar geç gelirse ya da adı değişirse sayfa çizmeye devam
//      etmeli. Okuma tarafı WeeklyReport.tsx'teki iki küçük adaptörden geçer
//      (extractMessagingReasons / tableRows); hiçbir bileşen ham şekle doğrudan bakmaz.
//   2. Para alanları `null` — "değer yok" ile "sıfır" ASLA karışmaz (§6). BackOffice
//      erişilemezse payda yoktur ve yüzde UYDURULMAZ; DAVET sekmesinin para sütunu
//      hiç yoktur (`depositTracked:false`) → payı `null`, sıfır DEĞİL.
//
// Liste / sebep / temsilci adları SHEET DEĞERLERİDİR: Türkçe gelirler ve
// EN kaplamasında da OLDUĞU GİBİ basılırlar (kasıtlı — sözlüğe girmezler).

export interface WeeklySummary {
  calls: number;
  conversations: number;
  answerRatePct: number;
  deposit12h: number;
  deposit36h: number;
  /** BackOffice erişilemezse null → yüzde de null; sayı basılmaz. */
  websiteDeposit: number | null;
  contribution12hPct: number | null;
  contribution36hPct: number | null;
}

/** Sheet'ten okunan TÜRKÇE sonuç etiketi → adet. Bilinmeyen anahtar gelebilir. */
export type WeeklyResults = Record<string, number>;

/** Liste satırının ortak gövdesi (kimlik alanı WeeklyListRow'da). */
interface WeeklyRowBase {
  calls: number;
  conversations: number;
  answerRatePct: number;
  results: WeeklyResults;
  /** false = bu sekmenin para sütunu YOK (DAVET) → yatırım alanları null. */
  depositTracked?: boolean;
  deposit12h: number | null;
  deposit36h: number | null;
  depositShare12hPct: number | null;
  depositShare36hPct: number | null;
  /** Yatıran üye sayısı (görüşme satırında 24/48s hücresi > 0) ve BAŞARI = yatıran ÷ görüşme (görev #202).
   *  null = bölünecek bir şey yok (görüşme yok / DAVET) → BOŞ hücre. Alan yoksa eski sunucu. */
  deposited12h?: number | null;
  deposited36h?: number | null;
  successRate12hPct?: number | null;
  successRate36hPct?: number | null;
}

export interface WeeklyListRow extends WeeklyRowBase {
  /** Sekme adı, sheet'teki hâliyle (ör. 'ARAMA TALEPLERİ'). */
  list: string;
}

/** Liste tablosunun TEK satır şekli: her liste satırı buna indirgenir
 *  (WeeklyReport.tsx `tableRows`). `subLabel` boş dizge = üst etiket basılmaz
 *  (§6: değersiz etiket basılmaz). Para SÜTUNU olmayan sekmede paylar null. */
export interface WeeklyTableRowKind {
  key: string;
  label: string;
  subLabel: string;
  calls: number;
  conversations: number;
  answerRatePct: number;
  results: WeeklyResults;
  depositShare12hPct: number | null;
  depositShare36hPct: number | null;
  deposited12h: number | null;
  deposited36h: number | null;
  successRate12hPct: number | null;
  successRate36hPct: number | null;
}

export interface WeeklyAgentRow {
  name: string;
  /** Gerçek arama sayısı = Arama Yapılmadı HARİÇ tüm sonuçlar (BULUNAMADI ve tanınmayan da
   *  hariç) — liste tablosunun "no-call düşülmüş" tabanının bu temsilciye düşen dilimi. */
  calls: number;
  conversations: number;
  /** BAŞARI ORANI'nın paydası = görüşme − DAVET (görev #297; DAVET'in yatırım penceresi yok). Alan yoksa eski sunucu →
   *  `conversations`. */
  successBase?: number;
  deposited12h: number;
  deposited36h: number;
  successRate12hPct: number;
  successRate36hPct: number;
  deposit12h: number;
  deposit36h: number;
  /** Conversations whose 24/48 h deposit cell is still empty (not due yet in the current week). Absent from an
   *  older server. */
  pendingCells12h?: number;
  pendingCells36h?: number;
  /** qms-panel's quality scores for the SAME week, matched by folded name. `null` =
   *  this agent has no evaluation this week (or the QMS read failed) — NOT a zero. */
  qms?: WeeklyAgentQms | null;
  /** Bu hafta gönderilen SMS (Postgres `sms_events`, ada göre eşleşir). `{sent:0}` gerçek
   *  cevaptır ("hiç atmadı"); `null` = SMS okunamadı. Alan yoksa eski sunucu. */
  sms?: WeeklyAgentSms | null;
  /** Bu hafta softphone ölçümü (agent_day_summary, ada göre eşleşir). `null` = bu hafta oturumu
   *  yok / okunamadı → BOŞ hücre, asla 0. Alan yoksa eski sunucu. */
  talk?: WeeklyAgentTalk | null;
}

export interface WeeklyAgentTalk {
  dials: number;
  connected: number;
  talkSec: number;
  /** Bağlanan çağrı yoksa null. */
  avgTalkSec: number | null;
}

/** Marka konuşma süresi bloğu: ölçülmediyse (o hafta hiç oturum yok) measured:false. */
export type WeeklyTalk =
  | { available: true; measured: true; dials: number; connected: number; talkSec: number; avgTalkSec: number | null;
      unmatched: ({ name: string } & WeeklyAgentTalk)[] }
  | { available: true; measured: false }
  | { available: false; measured: false; error: { kind: string; message: string } };

export interface WeeklyAgentSms {
  sent: number;
  failed: number;
}

/** Marka SMS toplamı; `unmatched` = bu hafta çağrı satırı olmayan (karnede görünmeyen) temsilciler. */
export type WeeklySms =
  | { available: true; sent: number; failed: number; unmatched: { name: string; sent: number; failed: number }[] }
  | { available: false; error: { kind: string; message: string } };

/** Haftalık SMS sayaçları (görev #189): temsilci (sms_events) + otomatik (kayit-oto / prime-kayit-ftd, migration 032).
 *  Ölçülmeyen değer null — asla 0 basılmaz. */
export interface WeeklySmsSummary {
  /** false = temsilci SMS'i okunamadı (o zaman total de null). */
  available: boolean;
  agent: { sent: number; failed: number; byTemplate: Record<string, number> } | null;
  /** null → `autoReason`: 'none' = markada otomatik SMS yok · 'not_yet' = henüz veri gelmedi · 'error' = okunamadı. */
  auto: { sent: number; reg: number; ftd: number; since: string | null } | null;
  autoReason: '' | 'none' | 'not_yet' | 'error';
  total: number | null;
  /** Toplam bir şeyi dışarıda bırakıyor (otomatik haftanın tamamı için ölçülmedi). */
  totalPartial: boolean;
}

/** Per-agent QMS block. avgScore is over final_score, so a critical call counts as 0. */
export interface WeeklyAgentQms {
  evalCount: number;
  avgScore: number | null;
  criticalCount: number;
}

/** Brand-level QMS totals, or why they are missing. Computed from the ROWS, not by
 *  averaging the per-agent averages (which would weight 1 call like 40). */
export type WeeklyQms =
  | { available: true; evalCount: number; avgScore: number | null; criticalCount: number }
  | { available: false; error: { kind: string; message: string } };

export interface WeeklyTeamAverage {
  successRate12hPct: number;
  successRate36hPct: number;
}

export interface WeeklyMessaging {
  total: number;
  answerRatePct: number;
  byChannel: Record<string, number>;
  byAgent: Record<string, number>;
  byReason?: Record<string, number>;
}

/** Arama talebi girişi (SLACK / FORM / TAKİP — 2026-09-17'den beri her biri KENDİ sekmesi),
 *  kendi H-sütunu sebep dağılımıyla. `source` = sekme adı, bir SHEET DEĞERİDİR (sözlüğe
 *  girmez); `byReason` Slack/Form/Takip halkalarını besler. Aynı üç satır `lists[]`'te de
 *  var — bu ikinci bir sayım değil, aynı satırların ikinci görünümü. Rapor bu üç listeden
 *  hiçbirini taşımıyorsa alan HİÇ gelmez (undefined). */
export interface WeeklyCallRequestSource {
  source: string;
  total: number;
  conversations: number;
  byReason: Record<string, number>;
}

/** BONUS sekmesinin C sütunu kampanya kovası (RETENTION / CHURN / FREESPIN / …), büyükten
 *  küçüğe. `list` sheet'in BİREBİR metnidir (açık sözlük — sözlüğe girmez, katlanmaz;
 *  boş bant '(boş)'). Motorda BONUS satırının BÖLÜNTÜSÜ → toplamlar BONUS satırıyla yapısal
 *  uyuşur. Raporda BONUS listesi yoksa alan HİÇ gelmez (undefined); sekme okunamadıysa []. */
export interface WeeklyBonusList {
  list: string;
  calls: number;
  conversations: number;
  answerRatePct: number;
  deposit12h: number;
  deposit36h: number;
}

export interface WeeklyBrandReport {
  summary: WeeklySummary;
  lists: WeeklyListRow[];
  agents: WeeklyAgentRow[];
  teamAverage: WeeklyTeamAverage;
  /** Absent on a report served before the QMS integration shipped. */
  qms?: WeeklyQms;
  /** Absent on a report served before the SMS column shipped. */
  sms?: WeeklySms;
  /** Absent on a report served before the talk-time column shipped. */
  talk?: WeeklyTalk;
  /** SMS sayaçları (görev #189). Alan yoksa eski sunucu. */
  smsSummary?: WeeklySmsSummary;
  messaging: WeeklyMessaging;
  /** Sunucu yarısıyla EŞ ZAMANLI — geç gelirse Slack/Form/Takip kartları "henüz gelmiyor" der. */
  callRequests?: WeeklyCallRequestSource[];
  /** Sunucu yarısıyla EŞ ZAMANLI — geç gelirse "Bonus listeleri" kartı "henüz gelmiyor" der. */
  bonusLists?: WeeklyBonusList[];
  /** Rakamlara neden GÜVENİLMEYECEĞİNİ söyleyen satırlar: bulunamayan sekme, ya da bu haftanın
   *  yeniden taranamayıp son başarılı taramanın sunulması. Söylenecek bir şey yoksa alan DÜŞER. */
  caveats?: string[];
}

/** Bir markanın taraması başarısızsa rapor GENELİ ayakta kalır: yalnız o markanın
 *  yuvası hata taşır (fail-soft — diğer markanın sayıları yine gösterilir). */
export interface WeeklyBrandError {
  error: { kind: string; message: string };
}

export type WeeklyBrandSlot = WeeklyBrandReport | WeeklyBrandError;

/** Marka yuvası hata mı? (Precedent: aynı dosyadaki isOffDuty.) */
export function isWeeklyBrandError(x: WeeklyBrandSlot | null | undefined): x is WeeklyBrandError {
  return !!x && 'error' in x;
}

export interface WeeklyReportData {
  week: { start: string; end: string };
  generatedAt: string;
  /** false = hafta henüz kapanmadı ya da boş hücre var → sayılar değişebilir. */
  final: boolean;
  finalAfter: string;
  /** 'historicalFormats' | 'mutableSource' — bilinmeyen değer sessizce atlanır. */
  warnings: string[];
  brands: Partial<Record<BrandId, WeeklyBrandSlot>>;
}

// ── SMS (eklenti giden SMS kaydı — migration 022) ─────────────────────────────

export type SmsOutcome = 'sent' | 'failed';

/** Tek bir SMS gönderim kaydı (SMS sayfası Günlük görünümü). Alıcı telefonu + mesaj gövdesi
 *  sahibin kararıyla TAM saklanır (denetim); marka kapsamına göre süzülür. */
export interface SmsLogEntry {
  id: string;
  at: string;            // ISO an
  agentId: string;
  name: string;
  brand: BrandId;
  templateId: string;
  customerId: string;
  phone: string;
  body: string;
  msgLen: number;
  outcome: SmsOutcome;
  reason: string;        // '' başarıda; başarısızlıkta proxy'nin gerçek gateway sebebi
}

export interface SmsTemplateStat { templateId: string; sent: number; failed: number; total: number; }
export interface SmsReasonStat { reason: string; count: number; }
export interface SmsAgentStat { agentId: string; name: string; sent: number; failed: number; total: number; }
export interface SmsDayStat { day: string; sent: number; failed: number; }

export interface SmsStats {
  total: number;
  sent: number;
  failed: number;
  successRate: number;   // 0–100 tam sayı
  byTemplate: SmsTemplateStat[];
  byReason: SmsReasonStat[];
  byAgent: SmsAgentStat[];
  byDay: SmsDayStat[];
}

export interface SmsReport {
  range: { start: string; end: string };
  stats: SmsStats;
  logs: SmsLogEntry[];
  /** true = günlük LOG_LIMIT'e takıldı (istatistikler yine TÜM aralığı kapsar). */
  logTruncated: boolean;
}

/** Temsilci Detayı SMS kartındaki tek satır (agent-day yükünün `sms` alanı). */
export interface AgentSmsEntry {
  id: string;
  at: string;
  brand: BrandId;
  templateId: string;
  customerId: string;
  phone: string;
  body: string;
  msgLen: number;
  outcome: SmsOutcome;
  reason: string;
}

// ── WhatsApp (paylaşılan WhatsApp/Telegram dosyası, GET /api/whatsapp) ─────────────────────
// Veri Postgres'te DEĞİL: sunucu her istekte paylaşılan Google Sheet'in marka sekmesini okur
// (2 dk süreç-içi cache). Sonuç/sebep/kanal etiketleri sheet'in BİREBİR metnidir (katlanmaz);
// `key` yalnız renk için motor kanonudur ('other' = tanınmayan sonuç).

export interface WhatsappResultCount { result: string; key: StatusKey | string; count: number; }
export interface WhatsappAgentRow { name: string; total: number; answered: number; answerRatePct: number; }

export interface WhatsappStats {
  total: number;
  answered: number;
  answerRatePct: number;
  byResult: WhatsappResultCount[];
  byChannel: { channel: string; count: number }[];
  byReason: { reason: string; count: number }[];
  byAgent: WhatsappAgentRow[];
  byDay: { day: string; count: number }[];
}

export interface WhatsappRecord {
  id: string;
  /** Marka-yerel gün (sheet'te saat yok). */
  day: string;
  brand: BrandId;
  agent: string;
  channel: string;
  customerId: string;
  result: string;
  resultKey: StatusKey | string;
  reason: string;
  note: string;
}

/** Marka başına kaynak durumu — sıfır gösteren bir markanın NEDEN sıfır olduğunu söyler. */
export type WhatsappSourceStatus = 'ok' | 'missing' | 'unconfigured' | 'quota' | 'shifted' | 'error';

export interface WhatsappReport {
  range: { start: string; end: string };
  stats: WhatsappStats;
  logs: WhatsappRecord[];
  /** true = kayıtlar LOG_LIMIT'e takıldı (istatistikler yine TÜM aralığı kapsar). */
  logTruncated: boolean;
  sources: { brand: BrandId; tab: string; status: WhatsappSourceStatus }[];
}

// ── Winback (GET /api/winback — Görev #114, 2026-09-21) ─────────────────────────────────────
// Bir BackOffice müşterisinin kayıt/son giriş/son 10 yatırım bilgisi + tek TAKVİM AYI için
// yatırım kırılımları (haftanın günü / ayın haftası / günün saati), kullanılan bonuslar ve en
// çok oynanan 10 oyun. Veri CANLI BackOffice okumasıdır (Postgres'te hiçbir şey yok); her
// kaynak ayrı ayrı başarısız olabilir ve `sources` bunu ADIYLA söyler — okunamayan kart
// asla sıfır olarak çizilmez (`null` = okunamadı, 0 = müşteri hiç yapmadı).

// Görev #168 (2026-10-04): dönem `all` (tüm zamanlar) + son 10 çekim / oyun işlemi / kupon.
export type WinbackSourceKey =
  | 'profile' | 'deposits' | 'lastDeposits' | 'lastWithdrawals' | 'lastGames' | 'lastCoupons' | 'bonuses' | 'freespins' | 'games';
export type WinbackSourceStatus = 'ok' | 'not_found' | 'permission' | 'unconfigured' | 'throttled' | 'error';

export interface WinbackProfile {
  id: string;
  username: string;
  name: string;
  group: string;
  registerDate: string | null;
  lastLogin: string | null;
  balance: number | null;
  totalDeposit: number | null;
  totalWithdraw: number | null;
}

export interface WinbackDeposit { id: string; at: string; amount: number; provider: string; manual: boolean; }

export interface WinbackBucket { count: number; amount: number; }

export interface WinbackDeposits {
  count: number;
  total: number;
  /** ISO hafta günü 1 (Pazartesi) … 7 (Pazar) — yedi satır da her zaman gelir. */
  byWeekday: (WinbackBucket & { weekday: number })[];
  /** Ayın günü bantları: '1-7' | '8-15' | '16-23' | '24-31'. */
  byWeekOfMonth: (WinbackBucket & { key: string })[];
  /** Saat bantları (marka saati): '06-12' | '12-18' | '18-24' | '00-06', label = '06:01–12:00' … */
  byTimeOfDay: (WinbackBucket & { key: string; label: string })[];
  /** Takvim günü (yalnız yatırım olan günler). */
  byDay: (WinbackBucket & { day: string })[];
  /** Takvim ayı 'YYYY-MM' (yalnız yatırım olan aylar) — tüm zamanlar görünümünün kırılımı. */
  byMonth: (WinbackBucket & { month: string })[];
}

/** Son 10 oyun işlemi — BackOffice oyun işlemleri sekmesinin satırları (bahis + kazanç). */
export interface WinbackGameTx {
  id: string;
  at: string;
  /** Satırda ad alanı yoksa ''. */
  game: string;
  provider: string;
  kind: 'bet' | 'win' | 'other';
  /** Satırın kendi açıklaması ('Casino Bet' / 'Casino Win'). */
  label: string;
  amount: number | null;
  live: boolean;
  free: boolean;
}

/** Son 10 spor kuponu (/api/customer/couponlist, yalnız bu müşterinin satırları). */
export interface WinbackCoupon {
  id: string;
  ticketId: string;
  at: string;
  stake: number | null;
  possibleWin: number | null;
  /** Satırda ödeme alanı varsa; yoksa null. */
  win: number | null;
  coefficient: number | null;
  /** Spor bahsi sağlayıcısı ('Digitain' / 'Fcg' / 'Betco'); sağlayıcı listesi okunamadıysa ''. */
  provider?: string;
  /** Sağlayıcının KENDİ durum kolonu (digitainState / state / fcgStatus / status); 0 → null. */
  state: number | null;
  /** BackOffice'in kendi enum'undan TR etiket ('KAYBETTİ'); bilinmeyen kodda '' → '#N' basılır. */
  stateName: string;
  outcome?: 'open' | 'won' | 'lost' | 'cashout' | 'void' | null;
  open: boolean;
  freeBet: boolean;
  bonusFunded: boolean;
}

export interface WinbackBonus {
  /** BackOffice'in BİREBİR bonus adı. */
  title: string;
  kind: 'deposit' | 'freespin';
  count: number;
  /** deposit → toplam TL; freespin → toplam spin. */
  amount: number;
  cancelled: number;
}

export interface WinbackGame { name: string; provider: string; rounds: number; stake: number; win: number; live: boolean; }

/** Tüm zamanların altındaki aylık kırılım (sahip 2026-10-07) — aynı okumadan, ay başına aynı kartlar.
 *  `null` = kaynak okunamadı (asla 0). `gamesCoverage`: oyunlar tek sayfa bütçesiyle yeniden eskiye
 *  okunur → 'none' = bu ay hiç okunmadı, 'partial' = kısmen. */
export interface WinbackMonth {
  key: string;
  start: string;
  end: string;
  deposits: WinbackDeposits | null;
  bonuses: WinbackBonus[] | null;
  games: WinbackGame[] | null;
  gamesDistinct: number | null;
  gamesCoverage: 'full' | 'partial' | 'none' | null;
  active: boolean;
}

/** Bütün Aramalar (görev #193/#194) — bir müşteriye yapılan tek çağrı. Sunucu: src/api/customer-calls.js. */
export interface CustomerCallRow {
  id: string;
  /** ISO an (sunucu saati); `day`/`time` markanın yerel takvimi. */
  at: string;
  day: string;
  time: string;
  agent: string;
  list: string;
  listKey: string;
  /** Durum kanonu; '' = softphone çağrısı yapıldı ama sonuç KAYDEDİLMEDİ (`outcomeSaved:false`). */
  result: string;
  comment: string;
  /** tool = eklenti · manual = sheet'e elle · bot = DepositWatchdog damgası (temsilcinin eylemi değil). */
  source: 'tool' | 'manual' | 'bot';
  outcomeSaved: boolean;
  /** Softphone ölçümü (2.1.0+); oturum yoksa null → BOŞ. */
  talkSec: number | null;
  ringSec: number | null;
  answered: boolean | null;
}

export interface CustomerCallsResult {
  brand: BrandId;
  customerId: string;
  calls: CustomerCallRow[];
  /** En yeni `limit` satır gösterildi, daha eskiler var. */
  truncated: boolean;
  limit: number;
}

export interface WinbackReport {
  brand: BrandId;
  customerId: number;
  /** Dönem: takvim ayı ('YYYY-MM') ya da `allTime` (key 'all'). `capped` = pencereyi kayıt tarihi
   *  değil `maxMonths` sınırı kesti (sayfa "son N ay" der). */
  month: { key: string; start: string; end: string; tz: string; allTime: boolean; capped: boolean; maxMonths?: number };
  profile: WinbackProfile | null;
  lastDeposits: WinbackDeposit[] | null;
  lastDepositsScannedFrom: string | null;
  lastWithdrawals: WinbackDeposit[] | null;
  lastWithdrawalsScannedFrom: string | null;
  lastGames: WinbackGameTx[] | null;
  lastGamesMeta: { scannedFrom: string | null; truncated: boolean } | null;
  lastCoupons: WinbackCoupon[] | null;
  /** `accountWide` = BackOffice customerId filtresini yok saydı (başka müşterilerin kuponları
   *  süzüldü); `truncated` = bir dilim sayfa tavanına çarptı. */
  /** `providers` = sorulan spor bahsi sağlayıcıları (sahip 2026-10-07: her sağlayıcı ayrı okunur);
   *  `failedProviders` = okunamayanlar (kart yine diğerlerini gösterir). */
  lastCouponsMeta: {
    scannedFrom: string | null;
    truncated: boolean;
    accountWide: boolean;
    providers?: string[];
    providerSource?: 'list' | 'config' | 'none';
    failedProviders?: string[];
  } | null;
  deposits: WinbackDeposits | null;
  bonuses: WinbackBonus[] | null;
  games: WinbackGame[] | null;
  gamesMeta: { nameKey: string | null; distinct: number; rows: number; truncated: boolean; coveredFrom: string | null } | null;
  /** Yalnız tüm zamanlarda (en yeni ay önce); tek ay sorgusunda null. Eski sunucu alanı hiç vermez. */
  monthly?: WinbackMonth[] | null;
  sources: { key: WinbackSourceKey; status: WinbackSourceStatus; message?: string }[];
}

// ── Ayarlar → Panel Şifreleri (src/api/panel-passwords.js) ──────────────────
/** One agent who can sign in to the Call Agent panel on a brand (the proxy's phone map). */
export interface PanelPasswordAgent {
  name: string;
  /** true = the agent (or an admin) set a password; false = the default <AD>.123 still works. */
  customPassword: boolean;
  updatedAt: string | null;
  /** Locked for ~60s after 5 wrong attempts; a reset or a set unlocks it. */
  locked: boolean;
}

export interface PanelPasswordList {
  ok: boolean;
  error?: string;
  brand?: BrandId;
  defaultSuffix?: string;
  minLength?: number;
  maxLength?: number;
  agents?: PanelPasswordAgent[];
}

export interface PanelPasswordResult {
  ok: boolean;
  error?: string;
  name?: string;
  /** Reset only: the default the agent signs in with (then she must choose her own). */
  defaultPassword?: string;
  /** false = the proxy could not write its file; the change lives until its next restart. */
  persisted?: boolean;
  /** Aynı hesabın (ad + KOD) diğer markalardaki kopyası: aynı sıfırlama / şifre oraya da gitti mi. */
  also?: PanelSyncStep[];
}

// ── Panel Şifreleri → Yeni temsilci / DARK şifresi (src/api/new-hire.js, 2026-10-14) ──────────
export type NewHireUnit = 'CALL' | 'VIP' | 'PREVIP';

export interface NewHireInput {
  brand: BrandId;
  name: string;
  unit: NewHireUnit;
  /** getircall dahili numarası (KOD), ör. "695". */
  kod: string;
  /** SIP secret — getircall → Santral → Dahili → Şifre. CALL için zorunlu. */
  secret: string;
  /** false = santralde doğrulamadan kaydet (yalnız doğrulama yapılamadığında önerilir). */
  verify: boolean;
  /** Boş = varsayılan <AD>.123 + ilk girişte zorunlu değişiklik. */
  panelPassword?: string;
}

export interface NewHireStep {
  key: 'phone' | 'db' | 'password' | 'registry' | 'binding';
  status: 'done' | 'exists' | 'skipped' | 'warn' | 'error';
  text: string;
}

export interface NewHireResult {
  ok: boolean;
  error?: string;
  /** Santral doğrulaması YAPILAMADI (ret değil) — "doğrulamadan kaydet" önerilebilir. */
  canSkipVerify?: boolean;
  brand?: BrandId;
  name?: string;
  kod?: string;
  agentId?: string | null;
  steps?: NewHireStep[];
  /** Panel girişi: password null = kişisel şifre belirlendi (ekranda gösterilmez) ya da kayıt önceden vardı. */
  login?: { name: string; password: string | null; custom: boolean } | null;
}

export interface DarkSecretResult {
  ok: boolean;
  error?: string;
  canSkipVerify?: boolean;
  name?: string;
  text?: string;
  unchanged?: boolean;
  /** Aynı kişinin aynı dahilisi olan kardeş hat (Prime ↔ Prime VIP): oradaki telefon listesine de taşındı mı.
   *  Sunucu eşi src/api/new-hire.js propagateDarkSecret / siblingSecretStep. */
  siblings?: { brand: BrandId; status: NewHireStep['status']; text: string }[];
}

// ── Liste Detayı (GET /api/list-detail, görev #222 + kopyaları, 2026-10-14) ──────────────────

export type ListDetailPeriod = 'day' | 'week' | 'month';

/** Bir listenin bir dilimi (gün / sebep / dönem toplamı). Sunucu eşi src/engine/list-detail.js emptyStats. */
export interface ListDetailStats {
  /** Durumu girilmiş her satır (Arama Yapılmadı dahil) — sebep halkasının birimi. */
  rows: number;
  /** Gerçek çağrı (Arama Yapılmadı / BULUNAMADI hariç) — Haftalık Rapor'un Toplam Çağrı tabanı. */
  calls: number;
  answered: number;
  conversations: number;
  /** Görüşmeden sonra 24/48 saatte yatırım yapan (hücre > 0). */
  success12h: number;
  success36h: number;
  deposit12h: number;
  deposit36h: number;
  /** Görüşme hücresi henüz BOŞ (yazılmadı) — para hâlâ değişebilir. */
  pending12h: number;
  pending36h: number;
  /** Bu listedeki çağrıya bağlanan gönderilmiş SMS (panel, sms_events). */
  sms: number;
}

export interface ListDetailDay {
  day: string;
  future: boolean;
  /** null = o günün haftası okunamadı (bilinmiyor — 0 DEĞİL) ya da gelecek gün. */
  stats: ListDetailStats | null;
  sms: number | null;
}

export interface ListDetailReason extends ListDetailStats {
  reason: string;
  /** Bu sebebin sonuç dağılımı (rapor statüsü → satır; Arama Yapılmadı dahil) — #276. */
  results: Partial<Record<StatusKey, number>>;
}

export interface ListDetailList {
  key: string;
  tab: string;
  /** Okunan hiçbir haftada sekme yoktu. */
  missing: boolean;
  /** false = DAVET: 24/48 saat sütunu hiç yok → para hücreleri BOŞ. */
  depositTracked: boolean;
  hasReasons: boolean;
  totals: ListDetailStats;
  results: Partial<Record<StatusKey, number>>;
  days: ListDetailDay[];
  reasons: ListDetailReason[];
}

export interface ListDetailData {
  brand: BrandId;
  period: ListDetailPeriod;
  date: string;
  today: string;
  generatedAt: string;
  from: string;
  to: string;
  /** Dönemin bugüne kadarki her günü okundu. */
  complete: boolean;
  /** Para kesin: her hafta zaman olarak kapandı ve boş hücre yok. */
  settled: boolean;
  weeks: { start: string; status: 'ok' | 'busy' | 'error' | 'too_old'; settled: boolean; error?: { kind: string; message: string } }[];
  lists: ListDetailList[];
  total: ListDetailStats;
  sms: { available: boolean; unattributed?: number; failed?: number; error?: { kind: string; message: string } | null };
  caveats: string[];
}

// ── Bonus Kullanımı (GET /api/bonus-usage, sahip 2026-10-27) ─────────────────────────────────

/** Bir dilim. Sunucu eşi src/engine/bonus-usage.js emptyUsage + ratePct. */
export interface BonusUsageStats {
  /** Bonus listesinde görüşülen (sonuç: Olumlu / Olumsuz / Aranmak İstemiyor / Konuşma Esnasında Kapattı). */
  conversations: number;
  /** 48 saat dolmuş ve BackOffice'ten okunmuş. */
  checked: number;
  /** Aramadan sonraki 48 saatte bonusu kullanan (çevrim başladı ya da yeni bonus alındı). */
  used: number;
  /** 48 saat henüz dolmadı. */
  waiting: number;
  /** 48 saat doldu ama BackOffice okunamadı (yetki / kesinti) — 0 DEĞİL, bilinmiyor. */
  unread: number;
  /** 48 saatten epey sonra okundu (ilk kurulum) — sonradan kullanım da sayılmış olabilir. */
  late: number;
  /** used ÷ checked; hiç okunmamışsa null → BOŞ (§6). */
  ratePct: number | null;
}

export interface BonusUsageDay {
  day: string;
  future: boolean;
  /** false = özellik bu günden önce yoktu → BOŞ (0 değil). */
  covered: boolean;
  stats: BonusUsageStats | null;
}

export interface BonusUsageAgent extends BonusUsageStats {
  agentId: string;
  name: string;
}

export interface BonusUsageData {
  brand: BrandId;
  period: ListDetailPeriod;
  date: string;
  from: string;
  to: string;
  today: string;
  /** İlk kapsanan gün; null = iş henüz hiç çalışmadı. */
  since: string | null;
  windowHours: number;
  /** Okunamayanların en sık sebebi ('permission' | 'unconfigured' | 'throttled' | 'error' | 'turnover_missing' | ''). */
  unreadReason: string;
  generatedAt: string;
  lists: { tab: string; totals: BonusUsageStats; days: { day: string; stats: BonusUsageStats | null }[] }[];
  total: BonusUsageStats;
  days: BonusUsageDay[];
  agents: BonusUsageAgent[];
}
